/**
 * ReservationStatusChips - 복수선택 가능한 상태 필터 칩
 */

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface StatusChipDef {
  value: string;
  label: string;
  statuses: string[];
  borderColor: string;
  bgColor: string;
  textColor: string;
  activeRing: string;
}

const STATUS_CHIPS: StatusChipDef[] = [
  {
    value: "requested",
    label: "신청",
    statuses: ["REQUESTED", "CHECKING"],
    borderColor: "border-warning/50",
    bgColor: "bg-warning/10",
    textColor: "text-warning",
    activeRing: "ring-warning",
  },
  {
    value: "confirmed",
    label: "확정",
    statuses: ["CONFIRMED"],
    borderColor: "border-emerald-300",
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-700",
    activeRing: "ring-emerald-400",
  },
  {
    value: "cancelled",
    label: "취소",
    statuses: ["CANCELLED"],
    borderColor: "border-destructive/50",
    bgColor: "bg-destructive/10",
    textColor: "text-destructive",
    activeRing: "ring-destructive",
  },
  {
    value: "noshow",
    label: "노쇼",
    statuses: ["NO_SHOW"],
    borderColor: "border-muted-foreground/40",
    bgColor: "bg-muted",
    textColor: "text-muted-foreground",
    activeRing: "ring-muted-foreground",
  },
  {
    value: "completed",
    label: "완료",
    statuses: ["COMPLETED"],
    borderColor: "border-info/50",
    bgColor: "bg-info/10",
    textColor: "text-info",
    activeRing: "ring-info",
  },
];

interface ReservationStatusChipsProps {
  activeFilters: string[];
  statusCounts?: Record<string, number>;
  onToggle: (status: string) => void;
  onClearAll: () => void;
  onSetFilters: (statuses: string[]) => void;
}

export function ReservationStatusChips({
  activeFilters,
  statusCounts = {},
  onToggle,
  onClearAll,
  onSetFilters,
}: ReservationStatusChipsProps) {
  const isAllSelected = activeFilters.length === 0;

  const getCount = (statuses: string[]) =>
    statuses.reduce((sum, s) => sum + (statusCounts[s] || 0), 0);

  const handleChipClick = (chip: StatusChipDef) => {
    const isActive = chip.statuses.every((s) => activeFilters.includes(s));
    if (isActive) {
      // Remove these statuses
      const next = activeFilters.filter((s) => !chip.statuses.includes(s));
      onSetFilters(next);
    } else {
      // Add these statuses
      const next = [...new Set([...activeFilters, ...chip.statuses])];
      onSetFilters(next);
    }
  };

  const isChipActive = (chip: StatusChipDef) =>
    chip.statuses.every((s) => activeFilters.includes(s));

  return (
    <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide pb-0.5">
      {/* "전체" chip */}
      <button
        type="button"
        onClick={onClearAll}
        className={cn(
          "inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap",
          "hover:shadow-sm",
          isAllSelected
            ? "border-primary bg-primary/10 text-primary ring-1 ring-primary/30"
            : "border-border bg-background text-muted-foreground hover:bg-accent",
        )}
      >
        전체
      </button>

      {STATUS_CHIPS.map((chip) => {
        const active = isChipActive(chip);
        const count = getCount(chip.statuses);

        return (
          <button
            key={chip.value}
            type="button"
            onClick={() => handleChipClick(chip)}
            className={cn(
              "inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap",
              "hover:shadow-sm",
              active
                ? `${chip.borderColor} ${chip.bgColor} ${chip.textColor} ring-1 ${chip.activeRing}`
                : "border-border bg-background text-muted-foreground hover:bg-accent",
            )}
          >
            {chip.label}
            <Badge
              variant="secondary"
              className={cn(
                "h-4 min-w-4 px-1 text-[10px] leading-none",
                active ? `${chip.bgColor} ${chip.textColor}` : "",
              )}
            >
              {count}
            </Badge>
          </button>
        );
      })}
    </div>
  );
}

export { STATUS_CHIPS };
export type { StatusChipDef };
